import { Avatar as MantineAvatar, AvatarProps, Box, useMantineTheme, Tooltip } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import type { PresenceStatus } from '@actschurch/ccms-sdk';
import { usePresenceManager } from '@/hooks/usePresenceManager';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedAvatarProps extends AvatarProps {
  gradientBorder?: boolean;
  gradientVariant?: GradientVariant;
  borderWidth?: number;
  animated?: boolean;
  /** Manual status override (takes precedence over userId) */
  status?: 'online' | 'offline' | 'away' | 'busy';
  /** User ID to automatically fetch presence status */
  userId?: string;
  /** Show presence indicator */
  showPresence?: boolean;
  /** Show tooltip with presence info */
  showPresenceTooltip?: boolean;
}

const Avatar = forwardRef<HTMLDivElement, ThemedAvatarProps>(
  (
    {
      gradientBorder = false,
      gradientVariant = 'primary',
      borderWidth = 3,
      animated = false,
      status,
      userId,
      showPresence = false,
      showPresenceTooltip = false,
      style,
      size = 'md',
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const presenceManager = usePresenceManager();
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const statusColors = {
      online: '#22c55e',
      offline: '#9ca3af',
      away: '#f59e0b',
      busy: '#ef4444',
    };

    // Map PresenceStatus to status type (handles both numeric enum values and string representations)
    const mapPresenceStatus = (presenceStatus: PresenceStatus): 'online' | 'offline' | 'away' | 'busy' => {
      // PresenceStatus enum: Offline = 0, Online = 1, Away = 2, Busy = 3
      switch (presenceStatus) {
        case 0: // PresenceStatus.Offline
          return 'offline';
        case 1: // PresenceStatus.Online
          return 'online';
        case 2: // PresenceStatus.Away
          return 'away';
        case 3: // PresenceStatus.Busy
          return 'busy';
        default:
          return 'offline';
      }
    };

    // Determine final status (manual override or from presence)
    const finalStatus = useMemo(() => {
      // Manual status takes precedence
      if (status) return status;

      // If userId provided and showPresence is true, get from presence
      if (userId && showPresence) {
        const userPresence = presenceManager.onlineUsersMap.get(userId);
        if (userPresence) {
          return mapPresenceStatus(userPresence.status);
        }
      }

      return undefined;
    }, [status, userId, showPresence, presenceManager.onlineUsersMap]);

    // Get presence tooltip info
    const presenceTooltip = useMemo(() => {
      if (!showPresenceTooltip || !userId) return null;

      const userPresence = presenceManager.onlineUsersMap.get(userId);
      if (!userPresence) return null;

      const statusText = userPresence.isOnline ? userPresence.status : 'Offline';
      const activityText = userPresence.activityMessage ? ` - ${userPresence.activityMessage}` : '';

      return `${statusText}${activityText}`;
    }, [showPresenceTooltip, userId, presenceManager.onlineUsersMap]);

    if (!gradientBorder && !finalStatus) {
      return <MantineAvatar ref={ref} size={size} style={{ ...accessibilityStyles, ...style }} {...rest} />;
    }

    const gradient = theme.other.gradients[gradientVariant];
    const sizeMap: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = {
      xs: 24,
      sm: 32,
      md: 40,
      lg: 56,
      xl: 84,
    };
    const numericSize = typeof size === 'number' ? size : sizeMap[size as keyof typeof sizeMap] || 40;

    const avatarContent = (
      <Box
        ref={ref}
        style={{
          position: 'relative',
          display: 'inline-flex',
          padding: gradientBorder ? borderWidth : 0,
          borderRadius: '50%',
          background: gradientBorder ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})` : 'transparent',
          backgroundSize: animated ? '200% 200%' : '100%',
          animation: animated ? 'avatar-gradient 3s ease infinite' : undefined,
        }}
      >
        <MantineAvatar
          size={numericSize}
          radius="xl"
          style={{
            border: gradientBorder ? '2px solid var(--mantine-color-body)' : undefined,
            ...accessibilityStyles,
            ...style,
          }}
          {...rest}
        />
        {finalStatus && (
          <Box
            style={{
              position: 'absolute',
              bottom: borderWidth,
              right: borderWidth,
              width: numericSize / 4,
              height: numericSize / 4,
              borderRadius: '50%',
              backgroundColor: statusColors[finalStatus],
              border: '2px solid var(--mantine-color-body)',
            }}
          />
        )}
        {animated && (
          <style>{`
            @keyframes avatar-gradient {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
        )}
      </Box>
    );

    // Wrap with tooltip if presence tooltip is enabled
    if (presenceTooltip) {
      return (
        <Tooltip label={presenceTooltip} withArrow>
          {avatarContent}
        </Tooltip>
      );
    }

    return avatarContent;
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;
